import React, { useEffect, useState } from "react";
import axios from "axios";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Button as MuiButton,
  Grid,
  Link,
  Breadcrumbs as MuiBreadcrumbs,
  Card as MuiCard,
  CardContent as MuiCardContent,
  Divider as MuiDivider,
  Paper as MuiPaper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField as MuiTextField,
  Typography,
  Box,
  Checkbox,
  Menu,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { spacing } from "@mui/system";
import PDFViewerModal from "../../../pages/PDFViewer";
import { ArrowDropDown, Delete, Download, Search } from "@mui/icons-material";
import { Formik } from "formik";
import useAuth from "../../../hooks/useAuth";
import useAppDispatch from "../../../hooks/useAppDispatch";
import useAppSelector from "../../../hooks/useAppSelector";
import {
  createInvoice,
  deleteInvoice,
  deleteInvoicesBatch,
  fetchInvoices,
  generateInvoicesFromUnitList,
  openInvoiceForm,
  setInvoice,
  updateInvoice,
  updateInvoicesBatchPaid,
  updateInvoicesBatchReminder,
  updateInvoicesBatchSent,
} from "../../../redux/slices/invoice";
import { format } from "date-fns";
import { baseUrl } from "../../../config";
import { fetchAssociation } from "../../../redux/slices/association";
import CustomSnackbar from "../SnackBar";
import { hideSnackbar } from "../../../redux/slices/ui";

const Card = styled(MuiCard)(spacing);
const CardContent = styled(MuiCardContent)(spacing);
const Button = styled(MuiButton)(spacing);
const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);
const Paper = styled(MuiPaper)(spacing);
const TextField = styled(MuiTextField)<{ my?: number }>(spacing);

function InvoiceForm() {
  const dispatch = useAppDispatch();
  const { invoice } = useAppSelector((state) => state.invoice);
  const { currentUser } = useAuth();

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const handleSubmit = async (
    values: any,
    { resetForm, setSubmitting }: any
  ) => {
    if (invoice?.id) {
      dispatch(updateInvoice(invoice.id, values));
    } else {
      dispatch(createInvoice(values, associationId));
    }
    resetForm();
    setSubmitting(false);
    dispatch(openInvoiceForm(false));
  };

  return (
    <Formik
      initialValues={{
        invoiceNumber: invoice?.invoiceNumber || "",
        dueDate: invoice?.dueDate || "",
        total: invoice?.total || "",
      }}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, handleChange, handleSubmit }) => (
        <Card mb={6}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Lasku
            </Typography>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={6}>
                <Grid item md={4}>
                  <TextField
                    label="Laskun numero"
                    name="invoiceNumber"
                    value={values.invoiceNumber}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                <Grid item md={4}>
                  <TextField
                    label="Eräpäivä"
                    name="dueDate"
                    type="date"
                    value={values.dueDate}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    variant="outlined"
                    my={2}
                  />
                </Grid>
                <Grid item md={4}>
                  <TextField
                    label="Summa (€)"
                    name="total"
                    value={values.total}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    my={2}
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                mt={3}
                mr={2}
              >
                Tallenna
              </Button>
              <Button
                variant="contained"
                color="error"
                mt={3}
                onClick={() => dispatch(openInvoiceForm(false))}
              >
                Peruuta
              </Button>
            </form>
          </CardContent>
        </Card>
      )}
    </Formik>
  );
}

function InvoiceList({
  handleCreateInvoicesFromUnits,
  handleEditInvoice,
  handleShowInvoice,
  handleDelete,
  loading,
}: any) {
  const dispatch = useAppDispatch();
  const { association, status: associationStatus } = useAppSelector(
    (state) => state.association
  );
  const { invoices, status: invoiceStatus } = useAppSelector(
    (state) => state.invoice
  );
  const { currentUser } = useAuth();

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const isReady = associationStatus === "idle" && invoiceStatus === "idle";

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const allSelected =
    invoices?.length > 0 && selectedIds.length === invoices.length;

  const handleDownload = async (rowFile: any) => {
    const filename = rowFile.file.filename;
    try {
      const token = localStorage.getItem("accessToken");

      const response = await fetch(`${baseUrl}/files/download/${filename}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      // Create a blob from the response
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      // Create a link element, set the URL and click it
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename); // Set the file name for download
      document.body.appendChild(link);
      link.click();

      // Cleanup: remove the link and revoke the object URL
      link?.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading the file:", error);
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev: any) =>
      prev.includes(id) ? prev.filter((i: any) => i !== id) : [...prev, id]
    );
  };

  const handleBulkSend = async (ids: number[]) => {
    try {
      await dispatch(updateInvoicesBatchSent({ ids, associationId })).unwrap();
      // No need to show UI here; ui slice already did on fulfilled/rejected.
    } catch {
      // optional: nothing; error snackbar already shown by matcher
    }
  };

  const handleBulkReminder = async (ids: number[]) => {
    try {
      await dispatch(
        updateInvoicesBatchReminder({
          ids,
          associationId,
        })
      ).unwrap();
    } catch {
      // optional
    }
  };

  const handleBulkMarkPaid = async (ids: number[]) => {
    try {
      await dispatch(
        updateInvoicesBatchPaid({
          ids,
          associationId,
        })
      ).unwrap();
    } catch {
      // optional
    }
  };

  const handleBulkDelete = (ids: number[]) => {
    if (window.confirm(`Poistetaanko ${ids.length} laskua?`)) {
      dispatch(deleteInvoicesBatch(ids, associationId));
      setSelectedIds([]);
    }
  };

  const toggleSelectAll = () => {
    if (!invoices) return;
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(invoices.map((i: any) => i.id));
    }
  };

  const [actionMenuAnchorEl, setActionMenuAnchorEl] =
    useState<null | HTMLElement>(null);
  const isActionMenuOpen = Boolean(actionMenuAnchorEl);

  const openActionMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setActionMenuAnchorEl(event.currentTarget);
  };

  const closeActionMenu = () => {
    setActionMenuAnchorEl(null);
  };

  type InvoiceStatus = "draft" | "sent" | "reminded" | "paid";

  const getInvoiceStatusLabel = (status: InvoiceStatus): string => {
    switch (status) {
      case "draft":
        return "Luotu";
      case "sent":
        return "Lähetetty";
      case "reminded":
        return "Muistutus lähetetty";
      case "paid":
        return "Maksettu";
      default:
        return "Tuntematon";
    }
  };

  useEffect(() => {
    dispatch(fetchAssociation(associationId));
    return () => {
      dispatch(openInvoiceForm(false));
    };
  }, [dispatch, associationId]);

  return (
    <Card mb={6}>
      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          pb: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Laskut
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleCreateInvoicesFromUnits()}
              disabled={
                loading ||
                association?.iban === null ||
                association?.bic === null
              }
            >
              {loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Luo maksuunpanoluettelosta"
              )}
            </Button>
            {/* <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => handleEditInvoice(undefined, true)}
            >
              Lisää lasku
            </Button> */}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: selectedIds.length > 0 ? "#e3f2fd" : "#f5f5f5",
            borderRadius: 1,
            px: 2,
            py: 1.5,
            mt: 1,
          }}
        >
          <Typography variant="body2" fontWeight={500}>
            {selectedIds.length > 0
              ? `${selectedIds.length} laskua valittuna`
              : "Valitse laskuja käyttääksesi massatoimintoja"}
          </Typography>

          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={openActionMenu}
              disabled={selectedIds.length === 0}
              endIcon={<ArrowDropDown />}
            >
              Toiminnot
            </Button>

            <Menu
              anchorEl={actionMenuAnchorEl}
              open={isActionMenuOpen}
              onClose={closeActionMenu}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
              <MenuItem
                onClick={() => {
                  handleBulkSend(selectedIds);
                  closeActionMenu();
                }}
              >
                Lähetä lasku
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleBulkReminder(selectedIds);
                  closeActionMenu();
                }}
              >
                Lähetä muistutuslasku
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleBulkMarkPaid(selectedIds);
                  closeActionMenu();
                }}
              >
                Merkitse maksetuksi
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleBulkDelete(selectedIds);
                  closeActionMenu();
                }}
              >
                Poista
              </MenuItem>
            </Menu>
          </Box>
        </Box>
      </CardContent>
      <Paper>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  indeterminate={
                    selectedIds.length > 0 &&
                    selectedIds.length < invoices.length
                  }
                />
              </TableCell>
              <TableCell>Laskun numero</TableCell>
              <TableCell>Nimi</TableCell>
              <TableCell>Saaja</TableCell>
              <TableCell>Luontipäivä</TableCell>
              <TableCell>Eräpäivä</TableCell>
              <TableCell>Summa (€)</TableCell>
              <TableCell>Tila</TableCell>
              <TableCell>Toiminnot</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!isReady ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="50vh"
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Ei laskuja
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((row: any) => (
                <TableRow key={row.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(row.id)}
                      onChange={() => toggleSelect(row.id)}
                    />
                  </TableCell>
                  <TableCell>{row.invoiceNumber}</TableCell>
                  <TableCell>
                    {row.file?.originalname ?? "PDF puuttuu"}
                  </TableCell>
                  <TableCell>
                    {row.user.firstName + " " + row.user.lastName ||
                      "Ei tiedossa"}
                  </TableCell>
                  <TableCell>
                    {format(new Date(row.issueDate), "dd-MM-yyyy")}
                  </TableCell>
                  <TableCell>
                    {format(new Date(row.dueDate), "dd-MM-yyyy")}
                  </TableCell>
                  <TableCell>{row.total.toFixed(2)}</TableCell>
                  <TableCell>{getInvoiceStatusLabel(row.status)}</TableCell>
                  <TableCell>
                    <Download
                      style={{ cursor: "pointer" }}
                      onClick={() => handleDownload(row)}
                    />
                    <Search
                      style={{ cursor: "pointer" }}
                      onClick={() => handleShowInvoice(row, true)}
                    />
                    {/* <Edit
                    style={{ cursor: "pointer" }}
                    onClick={() => handleEditInvoice(row, true)}
                  /> */}
                    <Delete
                      style={{ cursor: "pointer" }}
                      onClick={() => handleBulkDelete([row.id])}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Card>
  );
}

function Invoice() {
  const dispatch = useAppDispatch();
  const { currentUser } = useAuth();
  const { snackbar } = useAppSelector((s) => s.ui);
  const { open } = useAppSelector((state) => state.invoice);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);

  const associationId =
    currentUser?.activeAssociation?.id || currentUser?.associations?.[0]?.id;

  const [loading, setLoading] = useState(false);

  const handleEditInvoice = (invoice: any, isOpen: boolean) => {
    dispatch(setInvoice(invoice));
    dispatch(openInvoiceForm(isOpen));
  };

  const handleShowInvoice = (invoice: any, isOpen: boolean) => {
    handleShowInvoiceModal(invoice, isOpen);
  };

  const handleDelete = (invoice: any) => {
    dispatch(deleteInvoice(invoice.id, associationId));
  };

  const handleShowInvoiceModal = (invoice: any, isOpen: boolean) => {
    const filename = invoice.file?.filename;
    if (!filename) return;

    setPdfUrl(`${baseUrl}/files/preview/${filename}`);
    setPdfOpen(isOpen);
  };

  const handleCreateInvoicesFromUnits = async () => {
    setLoading(true);
    try {
      const jobId = await dispatch(generateInvoicesFromUnitList(associationId));

      const interval = setInterval(async () => {
        const res = await axios.get(
          `${baseUrl}/invoices/status/${jobId.jobId}`
        );
        if (res.data.status === "completed") {
          clearInterval(interval);
          dispatch(fetchInvoices(associationId));
          setLoading(false);
        } else if (res.data.status === "failed") {
          clearInterval(interval);
          setLoading(false);
          alert("Laskujen luonti epäonnistui");
        }
      }, 3000);
    } catch (e: any) {
      console.error(e);
      setLoading(false);
    }
  };

  useEffect(() => {
    dispatch(fetchInvoices(associationId));
    return () => {
      dispatch(openInvoiceForm(false));
    };
  }, [dispatch, associationId]);

  return (
    <>
      <Helmet title="Laskut" />
      <Typography variant="h3" gutterBottom>
        Laskut
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Link component={NavLink} to="/">
          Etusivu
        </Link>
        <Typography>Laskut</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      <PDFViewerModal
        open={pdfOpen}
        onClose={() => setPdfOpen(false)}
        fileUrl={pdfUrl ?? ""}
        authToken={localStorage.getItem("accessToken")}
      />

      {open ? (
        <InvoiceForm />
      ) : (
        <InvoiceList
          handleCreateInvoicesFromUnits={handleCreateInvoicesFromUnits}
          handleShowInvoice={handleShowInvoice}
          handleEditInvoice={handleEditInvoice}
          handleDelete={handleDelete}
          loading={loading}
        />
      )}

      <CustomSnackbar
        open={Boolean(snackbar?.open)}
        message={snackbar?.message ?? ""}
        severity={snackbar?.severity ?? "info"}
        progress={100}
        duration={snackbar?.duration ?? 4000}
        onClose={() => dispatch(hideSnackbar())}
      />
    </>
  );
}

export default Invoice;
